import React from "react";
import Image from "next/image";
import { Star, MapPin } from "lucide-react";
import { WishlistButton } from "@/components/wishlist/WishlistButton";
import type { MappedHotel } from "@/types/hotel";

const fixImageUrl = (url: string | null): string => {
  if (!url) return "/images/hotels/placeholder.jpg";
  return url.replace(
    "https://assets.lilylanka.com",
    "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com",
  );
};

interface HotelCardProps {
  hotel: MappedHotel;
  onClick: (hotelId: number) => void;
}
export const HotelCard: React.FC<HotelCardProps> = ({ hotel, onClick }) => (
  <div
    className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-900 mb-4 relative cursor-pointer hover:shadow-lg transition-shadow duration-200 md:h-56"
    onClick={() => onClick(hotel.id)}
  >
    <div className="flex flex-col md:flex-row h-full">
      <div className="relative w-full md:w-64 h-48 md:h-full flex-shrink-0">
        <Image
          src={fixImageUrl(hotel.image)}
          alt={hotel.name}
          fill
          className="object-cover md:rounded-l-2xl"
          sizes="(max-width: 768px) 100vw, 256px"
          unoptimized
        />
        <div className="absolute top-3 right-3">
          <WishlistButton
            hotelUid={hotel.id}
            className="w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center hover:bg-white transition-colors"
            iconClassName="w-5 h-5 text-red-500"
          />
        </div>
      </div>

      <div className="flex-1 p-5 flex flex-col lg:flex-row lg:justify-between gap-4 overflow-hidden">
        <div className="flex-1 min-w-0 overflow-hidden">
          <h3 className="text-lg font-bold text-gray-900 mb-1.5 line-clamp-1">
            {hotel.name}
          </h3>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-3.5 h-3.5 ${
                    i < (hotel.stars || Math.floor(hotel.rating))
                      ? "fill-current"
                      : ""
                  }`}
                />
              ))}
            </div>
            <div className="flex items-start gap-1">
              <MapPin className="w-3.5 h-3.5 text-purple-900 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-blue-500">{hotel.location}</span>
            </div>
          </div>

          {hotel.amenities.length > 0 && (
            <div className="mb-1">
              <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                This property offers
              </p>
              <div className="flex flex-wrap gap-1.5 overflow-hidden max-h-[30px]">
                {hotel.amenities.slice(0, 3).map((amenity, index) => (
                  <span
                    key={index}
                    className="px-2.5 py-1 bg-gray-50 text-xs font-medium text-gray-700  whitespace-nowrap"
                  >
                    {amenity}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-row lg:flex-col justify-between lg:justify-between lg:items-end lg:text-right lg:min-w-[160px] lg:pl-5 lg:border-l border-gray-400 overflow-hidden">
          <p className="text-xs text-gray-400">
            {hotel.reviews > 0 ? `${hotel.reviews} reviews` : "No reviews yet"}
          </p>
          <div className="text-right">
            {hotel.availableQuantity && hotel.availableQuantity > 0 && (
              <div className="text-xs text-white font-bold bg-red-500 px-2 py-1 rounded mb-2 inline-block">
                Only {hotel.availableQuantity} room
                {hotel.availableQuantity > 1 ? "s" : ""} left
              </div>
            )}
            <div className="text-xl font-bold text-gray-900">
              {hotel.price > 0
                ? `LKR ${hotel.price.toLocaleString()}`
                : "Price on request"}
            </div>
            {hotel.cancellation && (
              <div className="text-xs text-green-600 font-medium mb-3">
                {hotel.cancellation}
              </div>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClick(hotel.id);
              }}
              className="mt-3 w-full lg:w-auto px-6 py-2.5 bg-purple-900 hover:bg-purple-700 text-white text-sm font-semibold rounded-xs transition-colors"
            >
              See available rooms
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);
